
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, Search, Filter, ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Poll } from '@/types/poll';
import { useCountry } from '@/contexts/country';
import useCategories from '@/hooks/useCategories';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';

interface PollsAdminControlsProps {
  polls: Poll[];
  setFilteredPolls: React.Dispatch<React.SetStateAction<Poll[]>>;
  onRecalculate: () => void;
  isRecalculating: boolean;
}

type SortOption = {
  label: string;
  value: string;
  direction: 'asc' | 'desc';
};

const PollsAdminControls = ({
  polls,
  setFilteredPolls,
  onRecalculate,
  isRecalculating
}: PollsAdminControlsProps) => {
  const isMobile = useIsMobile();
  const { availableCountries } = useCountry();
  const { categories } = useCategories();

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // Sorting state
  const [sortOption, setSortOption] = useState<SortOption>({
    label: 'Created (Newest)',
    value: 'created_at',
    direction: 'desc'
  });

  // Active filters count
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Apply filters and sorting
  useEffect(() => {
    let filteredResult = [...polls];
    let filtersCount = 0;

    // Apply search filter
    if (searchText) {
      filteredResult = filteredResult.filter(poll => 
        poll.title.toLowerCase().includes(searchText.toLowerCase()) || 
        (poll.description?.toLowerCase().includes(searchText.toLowerCase())) ||
        (poll.poll_number?.toLowerCase().includes(searchText.toLowerCase()))
      );
      filtersCount++;
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filteredResult = filteredResult.filter(poll => poll.status === statusFilter);
      filtersCount++;
    }

    // Apply country filter
    if (countryFilter !== 'all') {
      filteredResult = filteredResult.filter(poll => 
        countryFilter === 'global' ? 
          poll.country_code === null : 
          poll.country_code === countryFilter
      );
      filtersCount++;
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filteredResult = filteredResult.filter(poll => poll.category_id === categoryFilter);
      filtersCount++;
    }

    // Apply sorting
    filteredResult.sort((a, b) => {
      const fieldA = a[sortOption.value as keyof Poll];
      const fieldB = b[sortOption.value as keyof Poll];
      
      if (fieldA === fieldB) return 0;
      
      // Handle null/undefined values
      if (fieldA == null) return sortOption.direction === 'asc' ? -1 : 1;
      if (fieldB == null) return sortOption.direction === 'asc' ? 1 : -1;

      // Compare based on direction
      if (sortOption.direction === 'asc') {
        return fieldA < fieldB ? -1 : 1;
      } else {
        return fieldA > fieldB ? -1 : 1;
      }
    });

    setActiveFiltersCount(filtersCount);
    setFilteredPolls(filteredResult);
  }, [polls, searchText, statusFilter, countryFilter, categoryFilter, sortOption, setFilteredPolls]);

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setCountryFilter('all');
    setCategoryFilter('all');
  };

  const getSortOptions = (): SortOption[] => [
    { label: 'Created (Newest)', value: 'created_at', direction: 'desc' },
    { label: 'Created (Oldest)', value: 'created_at', direction: 'asc' },
    { label: 'Title (A-Z)', value: 'title', direction: 'asc' },
    { label: 'Title (Z-A)', value: 'title', direction: 'desc' },
    { label: 'Status', value: 'status', direction: 'asc' },
    { label: 'Responses (Highest)', value: 'total_votes', direction: 'desc' },
    { label: 'Responses (Lowest)', value: 'total_votes', direction: 'asc' },
    { label: 'Poll Number', value: 'poll_number', direction: 'asc' },
  ];

  return (
    <div className={`space-y-2 ${isMobile ? 'w-full' : 'flex-1 mr-4'}`}>
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search polls..."
            className="pl-9"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* Recalculate Stats Button */}
        <Button 
          variant="outline" 
          onClick={onRecalculate}
          disabled={isRecalculating}
          className="whitespace-nowrap"
        >
          <RefreshCw size={16} className={`mr-2 ${isRecalculating ? 'animate-spin' : ''}`} />
          Recalculate Stats
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>

        {/* Country Filter */}
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="all">All Countries</SelectItem>
              <SelectItem value="global">Global Only</SelectItem>
              {availableCountries.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {/* Category Filter */}
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {/* Sort Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <span>Sort</span>
              <ChevronDown size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Sort By</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {getSortOptions().map(option => (
                <DropdownMenuItem 
                  key={`${option.value}-${option.direction}`}
                  onClick={() => setSortOption(option)}
                  className={sortOption.label === option.label ? "bg-muted" : ""}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Clear Filters Button - only show if there are active filters */}
        {activeFiltersCount > 0 && (
          <>
            <Badge variant="secondary" className="h-9 px-3 flex items-center">
              {activeFiltersCount} {activeFiltersCount === 1 ? 'filter' : 'filters'} active
            </Badge>
            <Button 
              variant="ghost" 
              className="h-9" 
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PollsAdminControls;
